<script>
import { 
  IconClose, 
  IconCheckCircleFill, 
  IconInfoCircleFill, 
  IconExclamationCircleFill, 
  IconCloseCircleFill 
} from '@arco-design/web-vue/es/icon'
import ZlButton from '@/components/ZlButton.vue'

export default {
  name: 'ZlModal',
  components: {
    IconClose,
    IconCheckCircleFill,
    IconInfoCircleFill,
    IconExclamationCircleFill,
    IconCloseCircleFill,
    ZlButton
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'success', 'info', 'warning', 'error', 'confirm'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: '520px'
    },
    closable: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    okText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    okLoading: {
      type: Boolean,
      default: false
    },
    onOk: {
      type: Function,
      default: null
    },
    onCancel: {
      type: Function,
      default: null
    },
    onClose: {
      type: Function,
      default: null
    },
    onDestroy: {
      type: Function,
      default: null
    }
  },
  emits: ['update:visible', 'ok', 'cancel', 'close'],
  data() {
    return {
      isAnimating: false
    }
  },
  computed: {
    modalWidth() {
      if (typeof this.width === 'number') {
        return `${this.width}px`
      }
      return this.width
    },
    typeIcon() {
      const iconMap = {
        success: 'IconCheckCircleFill',
        info: 'IconInfoCircleFill',
        warning: 'IconExclamationCircleFill',
        error: 'IconCloseCircleFill',
        confirm: 'IconExclamationCircleFill'
      }
      return iconMap[this.type] || null
    },
    typeClass() {
      return this.type !== 'default' ? `zl-modal-${this.type}` : ''
    },
    iconColor() {
      const colorMap = {
        success: '#52c41a',
        info: '#1890ff',
        warning: '#faad14',
        error: '#ff4d4f',
        confirm: '#faad14'
      }
      return colorMap[this.type] || '#666'
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.isAnimating = true
        this.$nextTick(() => {
          document.body.style.overflow = 'hidden'
        })
      } else {
        document.body.style.overflow = ''
        setTimeout(() => {
          this.isAnimating = false
        }, 300)
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    // 处理遮罩点击
    handleMaskClick(e) {
      if (this.maskClosable && e.target === e.currentTarget) {
        this.handleClose()
      }
    },

    // 处理关闭
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
      if (this.onClose) {
        this.onClose()
      }
    },

    // 处理确定
    handleOk() {
      this.$emit('update:visible', false)
      this.$emit('ok')
      if (this.onOk) {
        this.onOk()
      }
    },

    // 处理取消
    handleCancel() {
      this.$emit('update:visible', false)
      this.$emit('cancel')
      if (this.onCancel) {
        this.onCancel()
      }
    },

    // 键盘事件处理
    handleKeydown(e) {
      if (e.key === 'Escape' && this.closable) {
        this.handleClose()
      }
    }
  }
}
</script>

<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="visible || isAnimating"
        class="zl-modal-mask"
        @click="handleMaskClick"
      >
        <transition name="modal-zoom">
          <div
            v-if="visible"
            class="zl-modal"
            :class="typeClass"
            :style="{ width: modalWidth }"
            @click.stop
          >
            <!-- 模态框头部 -->
            <div v-if="title || closable" class="zl-modal-header">
              <div class="zl-modal-title-wrapper">
                <!-- 类型图标 -->
                <div v-if="showIcon && typeIcon" class="zl-modal-icon">
                  <component 
                    :is="typeIcon" 
                    :style="{ color: iconColor, fontSize: '20px' }"
                  />
                </div>
                <div class="zl-modal-title">{{ title }}</div>
              </div>
              <button
                v-if="closable"
                class="zl-modal-close"
                @click="handleClose"
              >
                <IconClose />
              </button>
            </div>

            <!-- 模态框内容 -->
            <div class="zl-modal-body">
              <div v-if="content" class="zl-modal-content">
                <!-- 内容区域的图标 -->
                <div v-if="showIcon && typeIcon && !title" class="zl-modal-content-icon">
                  <component 
                    :is="typeIcon" 
                    :style="{ color: iconColor, fontSize: '24px' }"
                  />
                </div>
                <div class="zl-modal-content-text">{{ content }}</div>
              </div>
              <slot></slot>
            </div>

            <!-- 模态框底部 -->
            <div v-if="showFooter" class="zl-modal-footer">
              <slot name="footer">
                <ZlButton v-if="showCancel" @click="handleCancel">{{ cancelText }}</ZlButton>
                <ZlButton 
                  variant="primary" 
                  :loading="okLoading"
                  @click="handleOk"
                >
                  {{ okText }}
                </ZlButton>
              </slot>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.zl-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  
  /* 毛玻璃背景 */
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.zl-modal {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  
  /* 毛玻璃效果 */
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  
  /* 阴影效果 */
  box-shadow: 
    0 25px 80px rgba(0, 0, 0, 0.2),
    0 15px 50px rgba(0, 0, 0, 0.15),
    0 8px 25px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  
  overflow: hidden;
}

.zl-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.zl-modal-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.zl-modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.zl-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.zl-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #000000;
  transition: all 0.2s ease;
  font-size: 16px;
}

.zl-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
  color: #000000;
}

.zl-modal-body {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  color: #000000;
  line-height: 1.6;
}

.zl-modal-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  white-space: pre-line;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #000000;
}

.zl-modal-content-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.zl-modal-content-text {
  flex: 1;
}

.zl-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

/* 动画效果 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-zoom-enter-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.modal-zoom-leave-active {
  transition: all 0.25s ease-in;
}

.modal-zoom-enter-from {
  opacity: 0;
  transform: scale(0.7) translateY(-50px);
}

.modal-zoom-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

/* 不同类型模态框的样式 */
.zl-modal-success {
  border: 1px solid rgba(82, 196, 26, 0.3);
}

.zl-modal-info {
  border: 1px solid rgba(24, 144, 255, 0.3);
}

.zl-modal-warning {
  border: 1px solid rgba(250, 173, 20, 0.3);
}

.zl-modal-error {
  border: 1px solid rgba(255, 77, 79, 0.3);
}

.zl-modal-confirm {
  border: 1px solid rgba(250, 173, 20, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .zl-modal-mask {
    padding: 10px;
  }
  
  .zl-modal {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 12px;
  }
  
  .zl-modal-header {
    padding: 16px 20px 12px;
  }
  
  .zl-modal-title {
    font-size: 16px;
  }
  
  .zl-modal-body {
    padding: 16px 20px;
  }
  
  .zl-modal-footer {
    padding: 12px 20px 16px;
    flex-direction: column-reverse;
  }
  
  .zl-modal-footer .arco-btn {
    width: 100%;
  }
}

/* 滚动条样式 */
.zl-modal-body::-webkit-scrollbar {
  width: 6px;
}

.zl-modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.zl-modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.zl-modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>